/**
 * The spectacle layer (C0-VFX, the user: "good, but needs more spectacle"): what a landed blow adds on top of its own
 * ink, by weight — light for a plain cut (level 0), moderate for a break (1), heavy for a power move (2):
 * - a light flash where it lands (a pixel starburst, white with the kind's accent),
 * - sparks (streaks of the accent and gold), debris chips thrown up from the ground (1+),
 * - ground decals that fade: ink cracks (1+), a scorched ink pool with an accent rim (2).
 * The screen-space part (shake, zoom punch, hit-stop, vignette) is the game's `impact`/`cinematic` and the lab stage's.
 */
import { drawDrop, qiLook, type Stroke } from './brush';
import { clamp01, easeOut, hash2, span } from './noise';
import { INK_PALETTES, type InkKind } from './palette';
import { drawSplats, foeX, memo, P, splats, stroke, type InkLayer, type InkScene } from './samples';

export type Weight = 0 | 1 | 2;

/** Share of sparks, debris and motes drawn (1 full; the low tiers set it lower, render/ink/detail/fx.ts). */
let sparkShare = 1;
export function setSparkShare(q: number): void {
  sparkShare = q;
}
export const weightOf = (kind: InkKind | string): Weight => (kind === 'power' ? 2 : kind === 'break' ? 1 : 0);

const decals = memo((D, tdy) => {
  const cracks = (r: number, n: number, seed: number): Stroke[] =>
    Array.from({ length: n }, (_, i) => {
      const a = (i / n) * Math.PI * 2 + hash2(i, 1, seed) * 0.6;
      const r1 = r * (0.6 + 0.5 * hash2(i, 2, seed));
      const mid = 0.5 + (hash2(i, 3, seed) - 0.5) * 0.3;
      return {
        pts: [
          [D + Math.cos(a) * 4, tdy + Math.sin(a) * 1.8],
          [D + Math.cos(a + 0.25) * r1 * mid, tdy + Math.sin(a + 0.25) * r1 * mid * 0.45],
          [D + Math.cos(a - 0.1) * r1, tdy + Math.sin(a - 0.1) * r1 * 0.45],
        ],
        w: 3.5,
        shape: 'flick',
        dry: 0.3,
        noHalo: true,
        seed: seed + i,
      };
    });
  return {
    // Scale pass (C0-VFX-PREP): about twice the old reach, more arms.
    small: cracks(34, 7, 1100),
    big: cracks(60, 11, 1110),
  };
});

const POOLS = new Map<string, HTMLCanvasElement>();
/** A flat ink pool (iso ellipse) with a ragged accent rim, `r` art px, as a cached canvas (2 px per art px). */
function pool(kind: InkKind, r: number): HTMLCanvasElement {
  const k = `${kind}${r}`;
  let c = POOLS.get(k);
  if (c) return c;
  const P2 = INK_PALETTES[kind];
  const W = r * 4 + 4;
  const H = r * 2 + 4;
  c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const g = c.getContext('2d')!;
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      const dx = (x + 0.5 - W / 2) / (r * 2);
      const dy = (y + 0.5 - H / 2) / r;
      const d = Math.hypot(dx, dy) + (hash2(Math.floor(Math.atan2(dy, dx) * 6), 1, 1120) - 0.5) * 0.18;
      if (d > 1) continue;
      g.fillStyle = d > 0.88 ? P2.acc : d > 0.7 ? P2.ink : (x + y) % 3 === 0 ? P2.inkLit : P2.ink;
      g.fillRect(x, y, 1, 1);
    }
  POOLS.set(k, c);
  return c;
}

const SPARKS = Array.from({ length: 36 }, (_, i) => ({
  a: (i / 36) * Math.PI * 2 + hash2(i, 1, 1130) * 0.5,
  v: 130 + 190 * hash2(i, 2, 1130),
  gold: hash2(i, 3, 1130) < 0.5,
}));
const DEBRIS = splats(1140, 20, -Math.PI / 2, 2.4, 210, 3, 0.3);

/** The flash: a four-point starburst with diagonal rays, white over the accent, for 0.1 s (bigger with the weight). */
function flash(g: CanvasRenderingContext2D, tau: number, sc: InkScene, x: number, y: number, w: Weight, kind: InkKind): void {
  if (tau < 0 || tau > 0.1) return;
  const P2 = INK_PALETTES[kind];
  const r = Math.round((10 + 14 * w) * (0.5 + easeOut(tau / 0.1)));
  const [sx, sy] = P(sc, x, y);
  const X = Math.round(sx);
  const Y = Math.round(sy);
  g.fillStyle = P2.acc;
  g.fillRect(X - r, Y - 1, r * 2 + 1, 3);
  g.fillRect(X - 1, Y - r, 3, r * 2 + 1);
  const d = Math.round(r * 0.6);
  for (let i = 1; i <= d; i++) {
    g.fillRect(X - i, Y - i, 1, 1);
    g.fillRect(X + i, Y - i, 1, 1);
    g.fillRect(X - i, Y + i, 1, 1);
    g.fillRect(X + i, Y + i, 1, 1);
  }
  g.fillStyle = '#ffffff';
  g.fillRect(X - r + 2, Y, r * 2 - 3, 1);
  g.fillRect(X, Y - r + 2, 1, r * 2 - 3);
  const c = 2 + w;
  g.fillRect(X - c, Y - c, c * 2 + 1, c * 2 + 1);
}

/** Sparks: short streaks of the accent and gold flying out and falling. */
function sparks(g: CanvasRenderingContext2D, tau: number, sc: InkScene, x: number, y: number, w: Weight, kind: InkKind): void {
  if (tau < 0 || tau > 0.4) return;
  const n = Math.round([12, 22, 36][w]! * sparkShare);
  const P2 = INK_PALETTES[kind];
  for (let i = 0; i < n; i++) {
    const s = SPARKS[i]!;
    if (hash2(i, 9, 1131) < tau * 2.2) continue;
    const r = s.v * (1 + w * 0.3) * (1 - Math.exp(-4 * tau)) / 4;
    const len = Math.max(1, Math.round(7 * (1 - tau / 0.4)));
    for (let k = 0; k < len; k++) {
      const [px, py] = P(sc, x + Math.cos(s.a) * (r - k), y + Math.sin(s.a) * (r - k) + 120 * tau * tau);
      g.fillStyle = k === 0 ? '#ffffff' : s.gold ? INK_PALETTES.power.acc : P2.acc;
      g.fillRect(Math.round(px), Math.round(py), 1, 1);
    }
  }
}

/**
 * A landed blow's spectacle `tau` s after it lands, on the foe (local `x`, `y`: where it hit; the foe's feet at
 * (`D`, `tdy`)): decals on the ground layer, flash, sparks and debris over.
 */
export function drawImpact(
  g: CanvasRenderingContext2D,
  tau: number,
  sc: InkScene,
  layer: InkLayer,
  w: Weight,
  kind: InkKind,
  x = foeX(sc),
  y = sc.tdy - 46,
): void {
  if (tau < 0 || tau > 1.8) return;
  if (layer === 'ground') {
    if (w >= 1) {
      const S = decals(sc);
      const list = w === 2 ? S.big : S.small;
      for (let i = 0; i < list.length; i++)
        stroke(g, list[i]!, kind, sc, easeOut(span(tau, i * 0.01, 0.06 + i * 0.01)), span(tau, 0.9, 1.7), 0, foeX(sc) - sc.D);
    }
    // The ink pool under a power blow is gone with the ink look (the user, 2026-10-10); the glowing cracks stay.
    if (w === 2 && !qiLook(kind)) {
      const c = pool(kind, 26);
      const fade = span(tau, 0.8, 1.6);
      if (fade < 1) {
        const [px, py] = P(sc, foeX(sc), sc.tdy);
        g.globalAlpha = 1 - Math.floor(fade * 3) / 3;
        g.drawImage(c, Math.round(px - c.width / 4), Math.round(py - c.height / 4), c.width / 2, c.height / 2);
        g.globalAlpha = 1;
      }
    }
    return;
  }
  flash(g, tau, sc, x, y, w, kind);
  sparks(g, tau, sc, x, y, w, kind);
  if (w >= 1) drawSplats(g, DEBRIS.slice(0, Math.round((w === 2 ? 20 : 12) * sparkShare)), 'earth', sc, foeX(sc), sc.tdy - 2, tau, sc.tdy + 2, 0.5, 320);
  // Gold motes drifting up after a power blow.
  if (w === 2 && tau > 0.05 && tau < 1)
    for (let i = 0; i < Math.round(18 * sparkShare); i++) {
      const u = clamp01((tau - 0.05 - i * 0.02) / 0.7);
      if (u <= 0 || u >= 1) continue;
      const [px, py] = P(sc, foeX(sc) + (hash2(i, 1, 1150) - 0.5) * 100, sc.tdy - 10 - u * 120);
      drawDrop(g, 'power', px, py, i % 3 ? 1 : 1.5, 'acc');
    }
}
